// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect, useMemo } from "react";
import {
  Mail,
  Tag,
  FileText,
  Save,
  X,
  Clock,
  Users,
  Database,
  Trash2,
  Loader2,
  Edit,
} from "lucide-react";
import { workflowStatusService } from "../../../services/workflowStatusService";
import toast from "react-hot-toast";

const EmailConfigurationForm = ({
  email: initialEmail,
  isNew = false,
  onSave,
  onCancel,
  onChange,
  onOpenTemplateEditor,
  onDelete,
  templateEditorLoading = false,
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [customRecipients, setCustomRecipients] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [emailValidationError, setEmailValidationError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEmail(initialEmail);
    if (initialEmail.custom_recipients) {
      setCustomRecipients(initialEmail.custom_recipients.join(", "));
    } else {
      setCustomRecipients("");
    }
  }, [initialEmail]);

  useEffect(() => {
    const loadTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const data = await workflowStatusService.getEmailTemplates();
        setTemplates(data.items || []);
      } catch (error) {
        console.error("Error loading templates:", error);
        setTemplates([]);
      }
      setTemplatesLoading(false);
    };

    loadTemplates();
  }, []);
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (field, value) => {
    const updatedEmail = { ...email, [field]: value };
    setEmail(updatedEmail);
  };

  const handleCustomRecipientsChange = (value) => {
    setCustomRecipients(value);
    setEmailValidationError("");

    if (!value.trim()) {
      const updatedEmail = { ...email, custom_recipients: [] };
      setEmail(updatedEmail);
      return;
    }

    const recipients = value
      .split(",")
      .map((r) => r.trim())
      .filter((r) => r);

    const invalidEmails = recipients.filter((email) => !validateEmail(email));

    if (invalidEmails.length > 0) {
      setEmailValidationError(
        `Ungültige E-Mail-Adressen: ${invalidEmails.join(", ")}`,
      );
    } else {
      setEmailValidationError("");
    }
    const updatedEmail = { ...email, custom_recipients: recipients };
    setEmail(updatedEmail);
  };

  const handleTemplateSelect = (template) => {
    const updatedEmail = {
      ...email,
      template_code: template.template_code,
      template_id: template.template_id,
    };
    setEmail(updatedEmail);
    setShowTemplateSelector(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    if (onChange) {
      Object.entries(email).forEach(([field, value]) => {
        onChange(field, value);
      });
    }

    if (email.custom_recipients && email.custom_recipients.length > 0) {
      const invalidEmails = email.custom_recipients.filter(
        (email) => !validateEmail(email),
      );
      if (invalidEmails.length > 0) {
        setEmailValidationError(
          `Ungültige E-Mail-Adressen: ${invalidEmails.join(", ")}`,
        );
        setIsSaving(false);
        return;
      }
    }

    try {
      await onSave(email);
      toast.success("E-Mail-Konfiguration gespeichert!");
    } catch (error) {
      console.error("Error saving email configuration:", error);
      toast.error("Fehler beim Speichern der E-Mail-Konfiguration.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    if (
      window.confirm(
        `Möchten Sie das E-Mail-Template "${email.notification_name}" wirklich löschen?`,
      )
    ) {
      onDelete(email);
    }
  };

  const selectedTemplate = useMemo(() => {
    if (!email.template_code || !templates.length) return null;

    return templates.find((t) => t.template_code === email.template_code);
  }, [email.template_code, templates]);

  const isFormValid = useMemo(() => {
    const hasNotificationName =
      email.notification_name && email.notification_name.trim() !== "";
    const hasTemplateCode =
      email.template_code && email.template_code.trim() !== "";
    const hasRecipientType = email.recipient_type;
    const hasNoErrors = !emailValidationError;

    return (
      hasNotificationName && hasTemplateCode && hasRecipientType && hasNoErrors
    );
  }, [
    email.notification_name,
    email.template_code,
    email.recipient_type,
    emailValidationError,
  ]);

  useEffect(() => {
    return () => {};
  }, []);

  const getIcon = (type) => {
    if (type === "customer") {
      return <Mail className="w-5 h-5 text-green-600 dark:text-green-400" />;
    } else if (type === "admin") {
      return <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
    } else {
      return <Mail className="w-5 h-5 text-green-600 dark:text-green-400" />;
    }
  };

  const getIconBg = (type) => {
    if (type === "customer") {
      return "bg-green-100 dark:bg-green-900/30";
    } else if (type === "admin") {
      return "bg-blue-100 dark:bg-blue-900/30";
    } else {
      return "bg-green-100 dark:bg-green-900/30";
    }
  };

  const getTitle = () => {
    if (isNew) {
      return "Neue E-Mail hinzufügen";
    }
    return email.notification_name;
  };

  const getDescription = () => {
    if (isNew) {
      return "Konfigurieren Sie eine neue E-Mail-Benachrichtigung";
    }
    return email.recipient_type === "customer"
      ? "Bestätigung an Kunden"
      : "Benachrichtigung an Admin";
  };

  return (
    <div
      className={`bg-white dark:bg-slate-800 border rounded-lg sm:p-6 p-4 shadow-sm dark:border-slate-600 ${isNew ? "border-green-200 dark:border-green-600 border-2" : "border-slate-200 dark:border-slate-600"}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 justify-between items-start mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center items-start gap-3">
          <div className={`p-2 ${getIconBg(email.recipient_type)} rounded-lg`}>
            {getIcon(email.recipient_type)}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
              {getTitle()}
            </h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {getDescription()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={email.is_enabled}
              onChange={(e) => handleChange("is_enabled", e.target.checked)}
              className="rounded border-gray-300 dark:border-slate-500 text-green-600 focus:ring-green-500 dark:bg-slate-700"
            />
            <span className="text-sm text-gray-700 dark:text-slate-300">
              Aktiv
            </span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={email.send_immediately}
              onChange={(e) =>
                handleChange("send_immediately", e.target.checked)
              }
              className="rounded border-gray-300 dark:border-slate-500 text-blue-600 focus:ring-blue-500 dark:bg-slate-700"
            />
            <span className="text-sm text-gray-700 dark:text-slate-300">
              Sofort senden
            </span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            <Tag className="w-4 h-4 inline mr-1" />
            Benachrichtigungsname
          </label>
          <input
            type="text"
            value={email.notification_name || ""}
            onChange={(e) => handleChange("notification_name", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-slate-700 dark:text-slate-200 transition-colors"
            placeholder="E-Mail Benachrichtigungsname"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            <FileText className="w-4 h-4 inline mr-1" />
            Template Code
          </label>
          <div className="flex gap-2">
            <div className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-gray-50 dark:bg-slate-700/50 text-gray-700 dark:text-slate-200">
              {email.template_code ? (
                <div className="flex items-center justify-between">
                  <span className="font-medium">{email.template_code}</span>
                </div>
              ) : (
                <span className="text-gray-500 dark:text-slate-400">
                  Bitte Template auswählen
                </span>
              )}
            </div>
            {email.template_id && (
              <input
                type="hidden"
                value={email.template_id}
                name="template_id"
              />
            )}
            {email.template_code && (
              <button
                type="button"
                onClick={() =>
                  onOpenTemplateEditor && onOpenTemplateEditor(email)
                }
                disabled={templateEditorLoading}
                className={`px-3 py-2 text-sm font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-md hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors flex items-center gap-2 ${
                  templateEditorLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                title="Template bearbeiten"
              >
                {templateEditorLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Edit className="w-4 h-4" />
                )}
                {templateEditorLoading ? "Lade..." : ""}
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowTemplateSelector(!showTemplateSelector)}
              className="px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-2"
            >
              <Database className="w-4 h-4" />
              <div className="xl:flex hidden">Auswählen</div>
            </button>
          </div>
          {selectedTemplate && (
            <div className="mt-1 text-xs text-green-600 dark:text-green-400">
              ✓ {selectedTemplate.template_name} (ID:{" "}
              {selectedTemplate.template_id})
            </div>
          )}
        </div>
      </div>

      {showTemplateSelector && (
        <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600">
          <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-3">
            Verfügbare Templates:
          </h4>
          {templatesLoading ? (
            <div className="text-slate-500 dark:text-slate-400">
              Lade Templates...
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-2">
              {templates.map((template) => (
                <button
                  key={template.template_id}
                  onClick={() => handleTemplateSelect(template)}
                  className={`text-left p-3 rounded-lg border transition-colors ${
                    email.template_code === template.template_code
                      ? "bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-800 dark:text-green-200"
                      : "bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600"
                  }`}
                >
                  <div className="font-medium text-sm text-slate-900 dark:text-slate-100">
                    {template.template_name}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {template.template_code}
                  </div>
                  <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    {template.variables?.length || 0} Variablen
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            <Mail className="w-4 h-4 inline mr-1" />
            Empfängertyp
          </label>
          <select
            value={email.recipient_type || "customer"}
            onChange={(e) => handleChange("recipient_type", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-slate-700 dark:text-slate-200 transition-colors"
          >
            <option value="customer">Kunde</option>
            <option value="booking_user">Buchender Nutzer</option>
            <option value="managers">Manager</option>
            <option value="assigned_manager">Zugewiesener Manager</option>
            <option value="operations_team">Operations Team</option>
            <option value="accounting">Buchhaltung</option>
            <option value="admin">Administrator</option>
            <option value="custom">Benutzerdefiniert</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            <Clock className="w-4 h-4 inline mr-1" />
            Verzögerung (Stunden)
          </label>
          <input
            type="number"
            value={email.delay_hours || 0}
            onChange={(e) =>
              handleChange("delay_hours", parseInt(e.target.value) || 0)
            }
            min="0"
            max="168"
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-slate-700 dark:text-slate-200 transition-colors"
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          <Users className="w-4 h-4 inline mr-1" />
          Empfänger (kommagetrennt)
        </label>
        <input
          type="text"
          value={customRecipients}
          onChange={(e) => handleCustomRecipientsChange(e.target.value)}
          className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:border-transparent dark:bg-slate-700 dark:text-slate-200 transition-colors ${
            emailValidationError
              ? "border-red-300 dark:border-red-500 focus:ring-red-500"
              : customRecipients && !emailValidationError
                ? "border-green-300 dark:border-green-600 focus:ring-green-500"
                : "border-gray-300 dark:border-slate-600 focus:ring-green-500"
          }`}
          placeholder="max@example.com, admin@example.com"
        />
        {emailValidationError && (
          <div className="mt-1 text-xs text-red-600 dark:text-red-400">
            {emailValidationError}
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row justify-between sm:items-center mt-6 gap-3 sm:gap-0">
        <div className="flex flex-wrap gap-3">
          <button
            onClick={onCancel}
            className="flex-1 min-w-[140px] sm:flex-none px-4 py-2 text-sm font-medium text-rose-700 dark:text-rose-200 border border-rose-300 dark:border-rose-700 bg-rose-100 dark:bg-rose-900/30 rounded-md hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors"
          >
            <X className="w-4 h-4 mr-2 inline" />
            Abbrechen
          </button>
          {!isNew && onDelete && (
            <button
              onClick={handleDelete}
              className="flex-1 min-w-[140px] sm:flex-none px-4 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-white dark:bg-slate-700 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-slate-600 transition-colors"
            >
              <Trash2 className="w-4 h-4 mr-2 inline" />
              Löschen
            </button>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={!isFormValid || isSaving}
          className="px-4 py-2 text-sm font-medium text-white bg-green-600 dark:bg-green-500 border border-green-600 dark:border-green-500 rounded-md hover:bg-green-700 dark:hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center sm:justify-start items-center gap-2"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2 inline" />
          )}
          {isNew ? "Hinzufügen" : "Speichern"}
        </button>
      </div>
    </div>
  );
};
export default EmailConfigurationForm;
